import React, { Component } from 'react'
import { reduxForm, Field, formValueSelector } from 'redux-form'
import { connect } from 'react-redux'
import { InputField, SelectField } from '../../../components/inputs'
import LoaderButton from '../../../components/loaderButton'
import times from 'lodash/times'
import uniqBy from 'lodash/uniqBy'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { ReduxFormProps } from '../../../forms'
import axios from 'axios'
import { getApiErrorMessage } from '../../../helpers/apiErrorHelper'
import Loader from '../../../components/loader'
import {
  selectCurrentRefiVehicle,
  selectCurrentRefiApplication,
  selectCurrentProductPackageId,
  selectProductPackage,
} from '../../../reducers'
import { clearVinRequiredError } from '../refiActions'
import { fetchVehicleOptions } from '../../../actions/vehicleOptionsActions'

// Capitalize words ≥4 letters and all-alpha; leave shorter or non-alpha words untouched.
const formatLabel = (label: string): string =>
  label &&
  label
    .split(/\s+/)
    .map((word) =>
      word.length < 4 || /[^A-Za-z]/.test(word)
        ? word
        : word[0].toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ')

// Map the human-readable condition label to the select option value
const CONDITION_LABEL_TO_VALUE: Record<string, string> = {
  Clean: 'CLEAN',
  Good: 'AVERAGE',
  Rough: 'ROUGH',
}

const parseConditionValue = (label?: string): string => {
  if (label && CONDITION_LABEL_TO_VALUE[label]) {
    return CONDITION_LABEL_TO_VALUE[label]
  } else if (label) {
    return label.toUpperCase()
  } else {
    return ''
  }
}

const formatTrimOptions = (
  options: VehicleOption[],
  year: string,
  make: string,
  model: string
) =>
  uniqBy(
    options.filter(
      (v) =>
        String(v.year) === year &&
        v.make?.toUpperCase() === make?.toUpperCase() &&
        v.model?.toUpperCase() === model?.toUpperCase()
    ),
    (v) => v.trim_id
  )
    .map((v) => ({
      value: String(v.trim_id),
      label: formatLabel(v.trim).trim() || 'Unknown',
    }))
    .sort((a, b) => (a.label && b.label ? a.label.localeCompare(b.label) : 0))

export const vinMask = () => {
  let mask: RegExp[] = []
  times(17, () => {
    mask.push(/[a-hj-npr-zA-HJ-NPR-Z0-9]/)
  })
  return mask
}
// mask for mileage input (numeric only)
const mileageMask = createNumberMask({ prefix: '' })

export interface FormProps {
  plate: string
  state: string
  vin: string
  year?: string
  make?: string
  model?: string
  trim_id?: string
  mileage?: string
  condition?: string
  transmission?: string
  used_new?: 'used' | 'new' | 'both'
  product_package_id?: string
}

// Type for vehicle option (from API: includes trim label and id)
interface VehicleOption {
  year: number
  make: string
  model: string
  trim: string // human-readable trim label
  trim_id: number // server-side trim identifier
  vins?: string[] // optional list of VINs for auto-lookup
}
interface ReduxFormStateProps {
  selectedYear?: string
  selectedMake?: string
  selectedModel?: string
  selectedTrimId?: string
  // current VIN form value
  vin?: string
  options: VehicleOption[]
  isLoading: boolean
  /** when true, outline the VIN field red with "VIN required" */
  vinRequiredError?: boolean
  productsNotAvailableError?: boolean
  productsNotAvailableMessage?: string | null
  productPackageStatus?: any
  clearVinRequiredError?: () => void
  /** lazily loads vehicle YMMT options for this wizard step */
  fetchVehicleOptions?: () => void
}
// component-local state for VIN lookup
interface PlateVinFormState {
  vinError?: string
  isLookingUpVin: boolean
  allowedTrimIds?: string[]
  validatedMake?: any
  validatedModel?: any
  validatedTrim?: any
}

export class PlateVinForm extends Component<
  ReduxFormProps<ReduxFormStateProps, FormProps>,
  PlateVinFormState
> {
  state: PlateVinFormState = {
    vinError: undefined,
    isLookingUpVin: false,
    allowedTrimIds: undefined,
    validatedMake: null,
    validatedModel: null,
    validatedTrim: null,
  }
  componentDidMount() {
    this.mountedRef.current = true
    // Load YMMT options only when the refi vehicle step actually renders,
    // and only if not already cached in the store (avoids redundant fetches).
    if (!this.props.isLoading && (this.props.options?.length ?? 0) === 0) {
      this.props.fetchVehicleOptions?.()
    }
  }
  componentDidUpdate(
    prevProps: ReduxFormProps<ReduxFormStateProps, FormProps>
  ) {
    const {
      vin,
      options,
      change,
      selectedYear,
      selectedMake,
      selectedModel,
      selectedTrimId,
    } = this.props

    // Handle VIN field changes.
    //
    // Compare through '' because the form value selector reads undefined until
    // redux-form registers the field, and that undefined -> '' transition is
    // initialization, not the agent clearing the VIN. Treating it as a change
    // wiped a prefilled Y/M/M/T on first update, which is how a new deal off
    // the CallTools deals modal lost its vehicle details.
    if ((prevProps.vin ?? '') !== (vin ?? '')) {
      this.setState({ vinError: undefined, allowedTrimIds: undefined })
      change('year', '')
      change('make', '')
      change('model', '')
      change('trim_id', '')
    }

    // Handle year field changes (clear dependent fields)
    // Only clear if changing FROM a previously-set value TO a new different value
    // (not during initial load when prevProps.selectedYear is empty/undefined)
    const yearChanged =
      prevProps.selectedYear &&
      prevProps.selectedYear !== selectedYear &&
      selectedYear
    if (yearChanged) {
      change('make', '')
      change('model', '')
      change('trim_id', '')
      this.setState({
        allowedTrimIds: undefined,
        validatedMake: null,
        validatedModel: null,
        validatedTrim: null,
      })
    }
    // Handle make field changes (clear dependent fields)
    // Only trigger if year hasn't changed (to avoid redundant cascades)
    // Only clear if changing FROM a previously-set value TO a new different value
    const makeChanged =
      prevProps.selectedMake &&
      prevProps.selectedMake !== selectedMake &&
      selectedMake &&
      !yearChanged
    if (makeChanged) {
      change('model', '')
      change('trim_id', '')
      this.setState({
        allowedTrimIds: undefined,
        validatedModel: null,
        validatedTrim: null,
      })
    }
    // Handle model field changes (clear trim)
    // Only trigger if year and make haven't changed (to avoid redundant cascades)
    // Only clear if changing FROM a previously-set value TO a new different value
    const modelChanged =
      prevProps.selectedModel &&
      prevProps.selectedModel !== selectedModel &&
      selectedModel &&
      !yearChanged &&
      !makeChanged
    if (modelChanged) {
      change('trim_id', '')
      this.setState({
        allowedTrimIds: undefined,
        validatedTrim: null,
      })
    }
    // when VIN field changes to a full 17-character VIN, auto-fill dropdowns
    if (prevProps.vin !== vin && vin && vin.length === 17) {
      const match = options.find((o) => o.vins?.includes(vin))
      if (match) {
        change('year', String(match.year))
        change('make', formatLabel(match.make))
        change('model', formatLabel(match.model))
        // auto-select trim by id
        this.setState({
          isLookingUpVin: false,
          allowedTrimIds: [String(match.trim_id)],
        })
        change('trim_id', String(match.trim_id))
      } else {
        // lookup via server
        this.setState({ isLookingUpVin: true, vinError: undefined })
        try {
          const tokenObj = JSON.parse(
            localStorage.getItem('accessToken') || 'null'
          )
          const accessToken = tokenObj?.access_token
          const url =
            `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/vehicle_by_vin` +
            `?access_token=${accessToken}&vin=${vin}`

          // safely set state only if still mounted
          const setStateIfMounted = (state) => {
            if (this.mountedRef.current) {
              this.setState(state)
            }
          }
          axios
            .get(url)
            .then((res) => {
              const data = res.data || {}
              if (data.year) change('year', String(data.year))

              setStateIfMounted({
                validatedMake: data.make,
                validatedModel: data.model,
              })

              if (data.make) change('make', data.make)
              if (data.model) change('model', data.model)
              if (Array.isArray(data.trims)) {
                // Use trims from VIN lookup rather than local saved options
                let allowedTrimIds = data.trims.map((t) =>
                  String(t.id || t.trim_id)
                )
                const matchedTrim = selectedTrimId
                  ? data.trims.find(
                      (t) =>
                        String(t.id || t.trim_id) === String(selectedTrimId)
                    )
                  : undefined
                setStateIfMounted({
                  allowedTrimIds,
                  ...(matchedTrim && {
                    validatedTrim: {
                      ...matchedTrim,
                      label: matchedTrim.name,
                    },
                  }),
                })

                if (matchedTrim) change('trim_id', selectedTrimId)

                // Always load trims into local options cache (single or multiple),
                // so they survive accordion collapse/remount without re-fetching.
                data.trims.forEach((trim) => {
                  const alreadyInOptions = options.some(
                    (o) => String(o.trim_id) === String(trim.id || trim.trim_id)
                  )
                  if (!alreadyInOptions) {
                    options.push({
                      year: data.year,
                      make: data.make,
                      model: data.model,
                      trim: trim.name,
                      trim_id: trim.id,
                    })
                  }
                })

                // if there is only one trim then auto-select it
                if (data.trims.length === 1) {
                  setStateIfMounted({
                    validatedTrim: {
                      ...data.trims[0],
                      label: data.trims[0].name,
                    },
                  })
                  change('trim_id', data.trims[0].id)
                }
              }
            })
            .catch((error) => {
              // The lookup returns 422 for a VIN it cannot decode AND for a
              // vehicle it is not allowed to touch, and those read very
              // differently to an agent -- the second is not their VIN being
              // wrong. It renders `{ error: ... }` rather than `{ message: ... }`,
              // which is why the reason never reached the field.
              //
              // Anything other than a 422 is not the agent's VIN being wrong --
              // a 500, a 403, a proxy 502 -- and used to set no error at all, so
              // the field sat blank and the lookup looked like it had simply not
              // happened. Those get the server's reason where there is one and a
              // neutral sentence where there is not, rather than being told the
              // VIN is invalid when it may be fine.
              const isDecodeRefusal = error.response?.status === 422

              setStateIfMounted({
                vinError: getApiErrorMessage(
                  error,
                  isDecodeRefusal
                    ? 'Invalid VIN'
                    : 'Could not check this VIN right now. Please try again.'
                ),
              })
            })
            .finally(() => {
              setStateIfMounted({ isLookingUpVin: false })
            })
        } catch (err) {
          this.setState({ isLookingUpVin: false })
        }
      }
    }

    // Auto-select trim when there's only one option available
    if (selectedYear && selectedMake && selectedModel) {
      let trimOptions = formatTrimOptions(
        options,
        selectedYear,
        selectedMake,
        selectedModel
      )

      // Add validated trim if it exists
      if (this.state.validatedTrim) {
        const { id, name } = this.state.validatedTrim
        const found = trimOptions.find((trim) => trim.value === String(id))
        if (!found) {
          trimOptions.push({
            value: String(id),
            label: name,
          })
        }
      }

      // Only apply the VIN trim filter when the backend returned concrete IDs.
      const hasAllowedTrimFilter = Boolean(this.state.allowedTrimIds?.length)
      let visibleTrimOptions = hasAllowedTrimFilter
        ? trimOptions.filter((opt) =>
            this.state.allowedTrimIds!.includes(opt.value)
          )
        : trimOptions

      // Auto-select if there's exactly one option and no trim is currently selected
      if (visibleTrimOptions.length === 1 && !selectedTrimId) {
        change('trim_id', visibleTrimOptions[0].value)
      }
    }
  }

  componentWillUnmount() {
    this.mountedRef.current = false
  }
  mountedRef: React.MutableRefObject<boolean> = { current: false }
  render() {
    const {
      vin,
      handleSubmit,
      submitting,
      selectedYear,
      selectedMake,
      selectedModel,
      options,
    } = this.props

    // derive dropdown options (years are numeric, convert to strings)
    const yearNums: number[] = Array.from(
      new Set(options.map((v) => v.year))
    ).sort((a, b) => b - a)
    const years = yearNums.map((y) => String(y))
    const makes = selectedYear
      ? uniqBy(
          options.filter((v) => String(v.year) === selectedYear),
          (v) => v.make?.toUpperCase()
        )
          .map((v) => formatLabel(v.make))
          .sort((a, b) => a.localeCompare(b))
      : []
    const models =
      selectedYear && selectedMake
        ? uniqBy(
            options.filter(
              (v) =>
                String(v.year) === selectedYear &&
                v.make?.toUpperCase() === selectedMake?.toUpperCase()
            ),
            (v) => v.model?.toUpperCase()
          )
            .map((v) => formatLabel(v.model))
            .sort((a, b) => (a && b ? a.localeCompare(b) : 0))
        : []
    // derive trim options: label = trim name, value = trim_id
    let trimOptions =
      selectedYear && selectedMake && selectedModel
        ? formatTrimOptions(options, selectedYear, selectedMake, selectedModel)
        : []

    const {
      allowedTrimIds,
      isLookingUpVin,
      validatedMake,
      validatedModel,
      validatedTrim,
    } = this.state

    // if a make, model or trim has been returned from VIN lookup
    // but isn't in the options then add it
    if (validatedMake) {
      const found = makes.find((make) => make == validatedMake)
      if (!found) {
        makes.push(formatLabel(validatedMake))
      }
    }

    if (validatedModel) {
      const found = models.find((model) => model == validatedModel)
      if (!found) {
        models.push(formatLabel(validatedModel))
      }
    }

    if (validatedTrim) {
      const { id, name } = validatedTrim
      const found = trimOptions.find((trim) => trim.value == id)
      if (!found) {
        trimOptions.push({
          value: String(id),
          label: name,
        })
      }
    }

    const hasAllowedTrimFilter = Boolean(allowedTrimIds?.length)

    // An empty trims array from VIN lookup means "no restriction", not "disable trim".
    let visibleTrimOptions = hasAllowedTrimFilter
      ? trimOptions.filter((opt) => allowedTrimIds!.includes(opt.value))
      : trimOptions

    const vinActive = Boolean(vin)
    const hasCompleteVin = Boolean(vin && vin.length === 17)
    const requireVin = this.props.vinRequiredError
    const disableYear = vinActive || requireVin
    const disableMake = vinActive || requireVin || !selectedYear
    const disableModel = vinActive || requireVin || !selectedMake
    const hasTrimSelectionContext = Boolean(selectedModel || hasCompleteVin)
    const disableTrim = isLookingUpVin || requireVin || !hasTrimSelectionContext

    // The server's own reason wins when it sent one. Not every 422 on the
    // package-create endpoint means "no products available" -- the duplicate-VIN
    // refusal and the verify-mileage copy land on the same flag, and painting
    // the hardcoded string over them is what left agents reading the wrong
    // sentence while the real one went to a toast.
    let vinError = this.state.vinError
    if (this.props.productsNotAvailableError) {
      vinError =
        this.props.productsNotAvailableMessage ||
        this.state.vinError ||
        'No products available for this vehicle'
    } else if (this.props.vinRequiredError) {
      vinError = 'VIN required'
    }

    return (
      <form
        className="form form--with-border"
        name="PlateVinForm"
        onSubmit={handleSubmit}
      >
        <div
          className="form__grid"
          // disabled={submitting} // TODO-TS
        >
          <div>
            <Field<any> // TODO-TS
              type="text"
              label="License Plate"
              name="plate"
              component={InputField}
              autoComplete="off"
              normalize={(val) => val?.toUpperCase().slice(0, 8)}
              maxLength={8}
            />
          </div>

          <div>
            <Field label="State" name="state" component={SelectField}>
              <option value="">Select state</option>
              <option value="AL">Alabama</option>
              <option value="AK">Alaska</option>
              <option value="AZ">Arizona</option>
              <option value="AR">Arkansas</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="CT">Connecticut</option>
              <option value="DE">Delaware</option>
              <option value="DC">District Of Columbia</option>
              <option value="FL">Florida</option>
              <option value="GA">Georgia</option>
              <option value="HI">Hawaii</option>
              <option value="ID">Idaho</option>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="IA">Iowa</option>
              <option value="KS">Kansas</option>
              <option value="KY">Kentucky</option>
              <option value="LA">Louisiana</option>
              <option value="ME">Maine</option>
              <option value="MD">Maryland</option>
              <option value="MA">Massachusetts</option>
              <option value="MI">Michigan</option>
              <option value="MN">Minnesota</option>
              <option value="MS">Mississippi</option>
              <option value="MO">Missouri</option>
              <option value="MT">Montana</option>
              <option value="NE">Nebraska</option>
              <option value="NV">Nevada</option>
              <option value="NH">New Hampshire</option>
              <option value="NJ">New Jersey</option>
              <option value="NM">New Mexico</option>
              <option value="NY">New York</option>
              <option value="NC">North Carolina</option>
              <option value="ND">North Dakota</option>
              <option value="OH">Ohio</option>
              <option value="OK">Oklahoma</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
              <option value="RI">Rhode Island</option>
              <option value="SC">South Carolina</option>
              <option value="SD">South Dakota</option>
              <option value="TN">Tennessee</option>
              <option value="TX">Texas</option>
              <option value="UT">Utah</option>
              <option value="VT">Vermont</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="WV">West Virginia</option>
              <option value="WI">Wisconsin</option>
              <option value="WY">Wyoming</option>
            </Field>
          </div>

          <div>
            <Field<any> // TODO-TS
              type="text"
              label="Vin"
              name="vin"
              component={InputField}
              mask={vinMask}
              guide={false}
              autoComplete="off"
              normalize={(val) => val?.toUpperCase()}
              fieldError={vinError}
              endAdornment={
                <Loader
                  isLoading={this.state.isLookingUpVin}
                  className="loader--inline"
                  style={{ height: '3rem' }}
                  black={true}
                />
              }
            />
          </div>
          <div>
            <Field
              label="Year"
              name="year"
              component={SelectField}
              disabled={disableYear}
            >
              <option value="">Select year</option>
              {years.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </Field>
          </div>
          <div>
            <Field
              label="Make"
              name="make"
              component={SelectField}
              disabled={disableMake}
            >
              <option value="">Select make</option>
              {makes.map((mk) => (
                <option key={mk} value={mk}>
                  {mk}
                </option>
              ))}
            </Field>
          </div>
          <div>
            <Field
              label="Model"
              name="model"
              component={SelectField}
              disabled={disableModel}
            >
              <option value="">Select model</option>
              {models.map((md) => (
                <option key={md} value={md}>
                  {md}
                </option>
              ))}
            </Field>
          </div>
          <div>
            <Field
              label="Trim"
              name="trim_id"
              component={SelectField}
              disabled={disableTrim}
            >
              <option value="">Select trim</option>
              {visibleTrimOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Field>
          </div>

          {/* Additional vehicle details */}
          <div>
            <Field<any>
              type="text"
              label="Current Mileage"
              name="mileage"
              component={InputField}
              mask={mileageMask}
              normalize={(val) => val.replace(/[^0-9]/g, '')}
            />
          </div>
          <div>
            <Field<any>
              label="Condition"
              name="condition"
              component={SelectField}
            >
              <option value="">Select condition</option>
              <option value="CLEAN">Clean</option>
              <option value="AVERAGE">Good</option>
              <option value="ROUGH">Rough</option>
            </Field>
          </div>
          <div>
            <Field<any>
              label="Transmission"
              name="transmission"
              component={SelectField}
            >
              <option value="">Select transmission</option>
              <option value="AUTOMATIC">Automatic</option>
              <option value="MANUAL">Manual</option>
              <option value="NA">Not applicable</option>
            </Field>
          </div>
          <div>
            <Field<any>
              label="Show rates for"
              name="used_new"
              component={SelectField}
            >
              <option value="used">Used</option>
              <option value="new">New</option>
              <option value="both">Both</option>
            </Field>
          </div>

          <div className="form__group filter__buttons">
            <LoaderButton
              type="submit"
              className="button button-drive-america"
              isLoading={submitting}
            >
              Next
            </LoaderButton>
          </div>
        </div>
      </form>
    )
  }
}

export const validate = (data) => {
  const { state, vin, plate, year, make, model, mileage, trim_id } = data || {}
  const errors: Partial<FormProps> = {}

  // state is always required
  if (!state) {
    errors.state = 'Select state required'
  }

  // mileage is always required
  if (!mileage) {
    errors.mileage = 'Mileage required'
  }

  const hasVin = !!vin
  const hasPlate = !!plate
  const hasYear = !!year
  const hasMake = !!make
  const hasModel = !!model
  const hasTrim = !!trim_id

  // trim is always required
  if (!hasTrim) {
    errors.trim_id = 'Trim required'
  }

  // if VIN path selected
  if (hasVin) {
    return errors
  }

  // if Plate path selected
  if (hasPlate) {
    return errors
  }

  // if Year/Make/Model group partially or fully selected
  if (hasYear || hasMake || hasModel) {
    if (!hasYear) {
      errors.year = 'Year required'
    }
    if (!hasMake) {
      errors.make = 'Make required'
    }
    if (!hasModel) {
      errors.model = 'Model required'
    }
    return errors
  }

  // no path selected: require at least one of VIN, Plate, or Year/Make/Model
  errors.vin = 'Vin required'
  errors.plate = 'License plate required'
  errors.year = 'Year required'
  errors.make = 'Make required'
  errors.model = 'Model required'

  return errors
}

const FORM_NAME = 'plateVinForm'
const selector = formValueSelector(FORM_NAME)

// Initialize from saved vehicle, and persist drafts on unmount
const ConnectedForm = reduxForm<FormProps, ReduxFormStateProps>({
  form: FORM_NAME,
  validate,
  enableReinitialize: true,
  destroyOnUnmount: false,
  // Keep user-edits on Y/M/M/T (and state) when initialValues shift (e.g. on VIN-required retry)
  keepDirtyOnReinitialize: true,
})(PlateVinForm)

const mapStateToProps = (state: any) => {
  const savedVehicle = selectCurrentRefiVehicle(state) || {}
  const productPackageId = selectCurrentProductPackageId(state)
  const pkg = selectProductPackage(state.productPackages, productPackageId)

  // A new deal off the CallTools deals modal has no vehicle to read yet: it
  // carries the synced vehicle's values only, so the agent starts from the same
  // details without the application pointing at that vehicle row (see
  // refiActions#prefillVehicleValuesInRefiApp). Values, so the same mapping
  // below serves both cases.
  const vehicle = savedVehicle.id
    ? savedVehicle
    : { ...selectCurrentRefiApplication(state).prefillVehicle }

  vehicle.year ??= vehicle.trimSyncMetadata?.year
  vehicle.make ??= vehicle.trimSyncMetadata?.make?.toUpperCase()
  vehicle.model ??= vehicle.trimSyncMetadata?.model?.toUpperCase()

  const initialValues: Partial<FormProps> = {
    plate: vehicle.plateNumber || vehicle.plate_number || '',
    state:
      vehicle.plateState?.toUpperCase() ||
      vehicle.plate_state?.toUpperCase() ||
      '',
    vin: vehicle.vin || '',
    year: vehicle.year != null ? String(vehicle.year) : '',
    make: vehicle.make ? formatLabel(vehicle.make) : '',
    model: vehicle.model ? formatLabel(vehicle.model) : '',
    trim_id:
      vehicle.selectedVehicleTrimId != null
        ? String(vehicle.selectedVehicleTrimId)
        : '',
    mileage: vehicle.mileage != null ? String(vehicle.mileage) : '',
    condition: parseConditionValue(vehicle.condition),
    transmission: vehicle.transmission
      ? vehicle.transmission.toUpperCase()
      : '',
    used_new:
      (pkg?.filters?.vehicle_condition as 'used' | 'new' | 'both') || 'used',
    product_package_id: productPackageId,
  }
  return {
    initialValues,
    selectedYear: selector(state, 'year'),
    selectedMake: selector(state, 'make'),
    selectedModel: selector(state, 'model'),
    selectedTrimId: selector(state, 'trim_id'),
    vin: selector(state, 'vin'),
    options: state.vehicleOptions.data,
    isLoading: state.vehicleOptions.isLoading,
    vinRequiredError: selectCurrentRefiApplication(state).vinRequiredError,
    productsNotAvailableError:
      selectCurrentRefiApplication(state).productsNotAvailableError,
    productsNotAvailableMessage:
      selectCurrentRefiApplication(state).productsNotAvailableMessage,
  }
}

export default connect(mapStateToProps, {
  clearVinRequiredError,
  fetchVehicleOptions,
})(ConnectedForm)
